/**
 * SarahSession — 服务端会话 API 封装（日期窗口制）
 *
 * - windowKey(): 当前日期窗口 day:YYYY-MM-DD（北京时间 6:00 切日）
 *   会话与页面解耦：每天连续对话，60min 无消息或跨窗口自动归档
 * - pageKey(): 当前页面标识（仅作划词召回的"页面 id"记录，不再决定会话）
 * - loadSession(): 载入当前窗口的活跃会话 + 消息（跨窗口/超时旧会话由服务端惰性归档）
 * - newTopic(): 手动开新话题（归档当前）
 * - searchArchives(): 归档搜索（关键词/日期，含划词内容）
 */

import app from 'flarum/forum/app';

/** 当前日期窗口 key（北京时间，<6:00 归前一日） */
export function windowKey() {
  const now = new Date(Date.now() + 8 * 3600 * 1000); // UTC+8
  const h = now.getUTCHours();
  if (h < 6) now.setUTCDate(now.getUTCDate() - 1);
  return 'day:' + now.toISOString().slice(0, 10);
}

/** 当前页面标识（划词召回记录用：规范化 /d/{id}-slug → /d/{id}） */
export function pageKey() {
  let p = (window.location && window.location.pathname) || '/';
  p = p.replace(/\/+$/, '') || '/';
  const m = p.match(/^\/d\/(\d+)/);
  if (m) return '/d/' + m[1];
  return p;
}

/**
 * 抓取当前页面主要内容，作为对话的背景数据发给模型。
 * 优先级：帖子正文 → 文章/课程内容 → #content 兜底；截断 6000 字。
 */
export function collectPageContent() {
  try {
    let text = '';
    const pick = (sel) => {
      const el = document.querySelector(sel);
      return el ? (el.innerText || '').trim() : '';
    };

    // 讨论页：所有楼层正文
    const posts = document.querySelectorAll('.Post-body');
    if (posts.length) {
      text = Array.from(posts)
        .slice(0, 6)
        .map((p, i) => '【楼层' + (i + 1) + '】' + (p.innerText || '').trim())
        .join('\n\n');
    }

    // 文章/课程内容
    if (!text) text = pick('.ArticleReaderPage-content');
    if (!text) text = pick('.CoursePage');
    if (!text) text = pick('.CourseDetailPage');

    // 兜底：#content 主体
    if (!text) text = pick('#content');

    text = (text || '')
      .replace(/\n{3,}/g, '\n\n')
      .replace(/\s+\n/g, '\n')
      .trim();

    if (text.length < 10) return ''; // 过短视为无有效内容
    return text.slice(0, 10000); // 划词答疑打包上限 1 万字
  } catch (e) {
    return '';
  }
}

function api(path) {
  return (app.forum.attribute('apiUrl') || '') + path;
}

function csrf() {
  return (app.session && app.session.csrfToken) || '';
}

/** 载入当前日期窗口的活跃会话：{session, messages[], archivedPrevious?, predecessor?} */
export async function loadSession() {
  const res = await fetch(api('/taro/ai/sarah/session'), {
    method: 'GET',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
  });
  if (!res.ok) throw new Error('HTTP ' + res.status);
  const json = await res.json();

  const attrs = (json.data && json.data.attributes) || {};
  return {
    session: {
      id: json.data.id,
      pageKey: attrs.pageKey,
      windowKey: attrs.windowKey,
      difyConversationId: attrs.difyConversationId || null,
      messageCount: attrs.messageCount || 0,
      totalTokens: attrs.totalTokens || 0,
      contextTokens: attrs.contextTokens || 0,
    },
    messages: (json.meta && json.meta.messages) || [],
    archivedPrevious: (json.meta && json.meta.archived_previous) || null,
    predecessor: (json.meta && json.meta.predecessor) || null,
  };
}

/** 手动开新话题：归档当前会话，返回新的空会话消息 */
export async function startNewTopic() {
  const res = await fetch(api('/taro/ai/sarah/session/new-topic'), {
    method: 'POST',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json', 'X-CSRF-Token': csrf() },
    body: JSON.stringify({
      data: { attributes: {} },
    }),
  });
  if (!res.ok) throw new Error('HTTP ' + res.status);
  const json = await res.json();
  return (json.meta && json.meta.messages) || [];
}

/** 归档搜索：{archives: [{id, pageKey, summary, endedAt, ...}]} */
export async function searchArchives(q, date) {
  const params = new URLSearchParams();
  if (q) params.set('q', q);
  if (date) params.set('date', date);
  const res = await fetch(api('/taro/ai/sarah/archives?' + params.toString()), {
    method: 'GET',
    credentials: 'include',
  });
  if (!res.ok) throw new Error('HTTP ' + res.status);
  const json = await res.json();
  const attrs = (json.data && json.data.attributes) || {};
  return attrs.archives || [];
}
